/**
 * Sprint Redesign v1 · Baustein 9 (Do 03.09.2026, 20:30).
 *
 * Fokustyp-Spiegel-Daten für /mein-muster — der User schaut in seinen eigenen
 * Spiegel. Fünf Perspektiven pro Fokustyp:
 *   - Verhaltensweisen (wie ticke ich im Alltag)
 *   - Erwartungshaltung (was erwarte ich von anderen)
 *   - Typische erste Impulse (was passiert automatisch)
 *   - Stressmuster (Zugang erzwingen · Rückzug · Resignation als individuelle Ausprägung)
 *   - Ableitbare Werte (was ist mir wichtig)
 *
 * Alle vier Fokustypen (Hund, Eule, Katze, Biene) nach Eule-Schema vollständig
 * befüllt. Andreas kann die Wording-Details im Walkthrough gegenchecken.
 */

export const FOKUSTYP_SPIEGEL = {
  eule: {
    ready: true,
    verhaltensweisen: [
      `Du analysierst, bevor du entscheidest — und willst wissen, warum etwas so ist.`,
      `Du liebst Systeme, Struktur und einen klaren roten Faden.`,
      `Du gehst Themen tief an, nicht breit — Qualität vor Tempo.`,
    ],
    erwartungshaltung: [
      `Andere sollten sich vorbereitet haben, bevor sie ein Anliegen an dich richten.`,
      `Argumente statt Emotionen. Erst die Fakten, dann alles Weitere.`,
      `Wenn du Zeit investierst, soll das Ergebnis Substanz haben.`,
    ],
    erste_impulse: [
      `„Wie hängt das mit dem Ganzen zusammen?" — Kontext einordnen.`,
      `Vor der Antwort: Fakten checken, Logik prüfen.`,
      `Bei Widersprüchen: präzise nachfragen, nicht schnell zustimmen.`,
    ],
    stressmuster: [
      `Zugang erzwingen: Du wirst zum Fakten-Bulldozer und drückst deine Analyse durch.`,
      `Rückzug: Du gehst innerlich raus, wenn Emotionen die Sache überlagern.`,
      `Resignation: „Bringt nichts, keiner will die Details wissen."`,
    ],
    stressmuster_tief: {
      wann: `In hitzigen Diskussionen, bei Druck von oben, wenn du keine fertige Analyse parat hast oder Emotionen die Sache überlagern.`,
      wie: `Du wirst still, dein Blick geht nach innen, du fängst an in deinem Kopf zu analysieren statt zu reagieren. Oder du schaltest um in Fakten-Modus und drückst deine Sicht durch — kühl, präzise, unerbittlich.`,
      erste_hilfe: `Sag „Ich brauche 10 Sekunden zum Nachdenken" — das gibt dir Raum ohne dass andere denken du blockst ab. Und wenn du in Fakten-Modus rutschst: erst spiegeln („Ich höre, dass dich das ärgert"), dann Fakten.`,
    },
    motivation: [
      `Ein Problem so tief verstehen, dass keine Frage mehr offen bleibt.`,
      `Zusammenhänge sehen, die andere übersehen.`,
      `Substanz statt Show — echte Erkenntnis statt schneller Antwort.`,
    ],
    blindspots: [
      `Du unterschätzt, wie sehr Menschen erst emotional andocken müssen, bevor sie Fakten hören.`,
      `Deine Genauigkeit wirkt auf andere leicht wie Rechthaben oder Klugscheißen.`,
      `Du wartest zu lange mit Entscheidungen, weil du noch ein Detail willst.`,
    ],
    werte: [
      `Klarheit — im Denken, im Sprechen, im Handeln.`,
      `Substanz — nicht Show, sondern Tiefe.`,
      `Verlässlichkeit — was du sagst, meinst du auch so.`,
      `Zeit-Souveränität — du entscheidest, wann du in die Tiefe gehst.`,
    ],
  },

  hund: {
    ready: true,
    verhaltensweisen: [
      `Du achtest zuerst darauf, wie es den anderen im Raum geht.`,
      `Du sorgst für ein gutes Miteinander — Beziehung ist dir wichtiger als Rechthaben.`,
      `Du bleibst auch in schwierigen Momenten freundlich und verbindlich.`,
    ],
    erwartungshaltung: [
      `Andere sollten aufeinander Rücksicht nehmen und den Ton halten.`,
      `Wenn etwas nicht passt, wünschst du dir eine ehrliche, aber weiche Ansage.`,
      `Du erwartest, dass Verabredungen und Zusagen gehalten werden.`,
    ],
    erste_impulse: [
      `„Alles gut bei dir?" — Stimmung im Raum checken.`,
      `Vor der eigenen Position: prüfen, wie es dem anderen gerade geht.`,
      `Bei Spannung: deeskalieren, den Ton weich halten, das Gespräch retten.`,
    ],
    stressmuster: [
      `Zugang erzwingen: Du wirst überfürsorglich und kümmerst dich, obwohl es niemand will.`,
      `Rückzug: Du schluckst runter, was dich stört — Hauptsache Harmonie.`,
      `Resignation: „Ich mach's halt allein, dann ist Ruhe."`,
    ],
    stressmuster_tief: {
      wann: `Wenn im Raum Spannung liegt, wenn jemand Wichtiges enttäuscht wirkt, wenn du zwischen zwei Loyalitäten stehst.`,
      wie: `Du schluckst deine eigene Meinung runter, lächelst weiter, springst ein, wo niemand fragt. Innen wird's eng, außen wirkt alles ruhig — bis irgendwann der Kragen platzt oder die Erschöpfung kommt.`,
      erste_hilfe: `Sag „Ich merke, ich will's gerade allen recht machen — kannst du mir eine Minute geben?" — das setzt einen weichen Anker und du bleibst im Kontakt, ohne dich selbst zu verlieren.`,
    },
    motivation: [
      `Das Miteinander so gestalten, dass sich alle gesehen fühlen.`,
      `Etwas Bleibendes für die Menschen um dich herum bauen.`,
      `Deine Nähe soll anderen Sicherheit geben — nicht nur Freundlichkeit.`,
    ],
    blindspots: [
      `Du sagst zu oft Ja, wo ein Nein für alle besser wäre.`,
      `Du liest Bedürfnisse anderer — und überliest die eigenen.`,
      `Konflikt-Vermeidung wirkt manchmal wie mangelnde Positionierung.`,
    ],
    werte: [
      `Verbundenheit — echtes Miteinander statt Nebeneinander.`,
      `Verlässlichkeit — auf dich kann man sich verlassen.`,
      `Wärme — Menschen sollen sich in deiner Nähe sicher fühlen.`,
      `Loyalität — du hältst zu den Menschen, die dir wichtig sind.`,
    ],
  },

  katze: {
    ready: true,
    verhaltensweisen: [
      `Du bleibst beweglich — du willst dir Optionen offenhalten, bis es passt.`,
      `Du sagst, was du denkst — auch dann, wenn's unbequem wird.`,
      `Du entscheidest lieber im Moment als lange vorher — Bauch und Gespür zählen.`,
    ],
    erwartungshaltung: [
      `Andere sollten dir Raum lassen — Anweisungen von oben mag dein Bauch nicht.`,
      `Wenn's ehrlich wird, erwartest du auch von anderen Ehrlichkeit — kein Schönreden.`,
      `Du erwartest, dass man auf Augenhöhe mit dir spricht — nicht von oben herab.`,
    ],
    erste_impulse: [
      `„Passt das gerade zu mir?" — dein Gespür zuerst befragen.`,
      `Bei Druck: kontern statt zurückweichen, Position markieren.`,
      `Bei Vorgabe von außen: erst prüfen, dann zusagen — nicht umgekehrt.`,
    ],
    stressmuster: [
      `Zugang erzwingen: Du wirst laut und emotional, damit deine Position gehört wird.`,
      `Rückzug: Du gehst raus aus dem Raum — physisch oder mental — bis der Druck weg ist.`,
      `Resignation: „Bringt eh nichts, die machen sowieso, was sie wollen."`,
    ],
    stressmuster_tief: {
      wann: `Wenn dir jemand von oben herab Anweisungen gibt, wenn du dich nicht ernst genommen fühlst, wenn dein Bauch schon Nein sagt aber alle weitermachen.`,
      wie: `Deine Stimme wird schärfer oder du wirst still und gehst innerlich raus. Manchmal beides in wenigen Sekunden. Der Fokus verengt sich auf „hier stimmt was nicht" und der Rest der Situation blendet aus.`,
      erste_hilfe: `Sag „Ich brauche 5 Minuten Luft, dann können wir weiterreden." Kein Beleidigt-Sein, kein Türknallen — einfach kurz raus, atmen, sortieren. Wenn du zurückkommst, weißt du meist genau, wo dein Nein liegt.`,
    },
    motivation: [
      `Sich selbst treu bleiben — auch wenn es unbequem wird.`,
      `Momente erleben, die sich echt anfühlen, nicht nur richtig aussehen.`,
      `Optionen offen halten, bis der richtige Weg klar wird.`,
    ],
    blindspots: [
      `Du wirkst auf andere manchmal unverbindlich, obwohl du nur Freiheit brauchst.`,
      `Direktheit ist deine Stärke — für andere ist sie oft ein Angriff.`,
      `Du merkst zu spät, wenn dein Rückzug andere verletzt.`,
    ],
    werte: [
      `Freiheit — dein eigener Rhythmus, deine eigene Entscheidung.`,
      `Echtheit — keine Fassade, kein Schönreden.`,
      `Selbstachtung — du machst dich nicht klein, um es anderen recht zu machen.`,
      `Lebendigkeit — Momente sollen sich richtig anfühlen, nicht nur richtig aussehen.`,
    ],
  },

  biene: {
    ready: true,
    verhaltensweisen: [
      `Du willst wissen, was konkret als Nächstes zu tun ist — und dann loslegen.`,
      `Du bringst Struktur rein: Wer macht was bis wann?`,
      `Du gehst Aufgaben in klaren Schritten an — nicht in Grundsatzdebatten.`,
    ],
    erwartungshaltung: [
      `Andere sollten mitziehen, wenn ein Fahrplan liegt — statt nachzudiskutieren.`,
      `Zusagen sind für dich verbindlich — du erwartest, dass sie eingehalten werden.`,
      `Du erwartest, dass Ergebnisse messbar sind — nicht nur Absichten.`,
    ],
    erste_impulse: [
      `„Was ist der nächste konkrete Schritt?" — sofort in Umsetzung denken.`,
      `Vor dem Grundsatzgespräch: To-do-Liste aufbauen, Verantwortung verteilen.`,
      `Bei Unklarheit: nachhaken, bis das Bild komplett ist — dann losgehen.`,
    ],
    stressmuster: [
      `Zugang erzwingen: Du übernimmst die Aufgaben-Verteilung, ob gewünscht oder nicht.`,
      `Rückzug: Du machst still weiter deine Liste ab, während die anderen noch reden.`,
      `Resignation: „Wenn hier keiner mitmacht, mach ich's halt allein."`,
    ],
    stressmuster_tief: {
      wann: `In Meetings die im Kreis drehen, wenn Zusagen nicht gehalten werden, wenn eine Grundsatzdebatte startet obwohl ein Fahrplan schon liegt.`,
      wie: `Du greifst zum Stift, verteilst die Aufgaben selbst, hakst innerlich ab und redest weniger. Manchmal wird's schneidend — kurze Sätze, klare Blicke, kein Small-Talk mehr. Andere spüren den Druck.`,
      erste_hilfe: `Sag „Bevor wir weiter planen — habe ich verstanden, was dir gerade wichtig ist?" Ein Moment Zuhören spart oft eine Stunde Umsteuern und hält deine Beziehungs-Konten voll.`,
    },
    motivation: [
      `Sichtbaren Fortschritt sehen — jede Woche ein Stück weiter.`,
      `Aus Chaos einen klaren Fahrplan machen, dem alle folgen können.`,
      `Zusagen halten und Ergebnisse liefern, auf die man sich verlassen kann.`,
    ],
    blindspots: [
      `Du übersiehst, dass andere manchmal reden müssen, bevor sie machen können.`,
      `Deine To-do-Logik wirkt für Beziehungs-Typen kalt oder ungeduldig.`,
      `Du erkennst zu spät, wenn ein Fahrplan an der Realität vorbeigeht.`,
    ],
    werte: [
      `Klarheit — jeder weiß, was zu tun ist, bis wann und mit welchem Ergebnis.`,
      `Wirksamkeit — Aufwand soll sich in Ergebnissen niederschlagen.`,
      `Verlässlichkeit — Zusage ist Zusage, Termin ist Termin.`,
      `Fortschritt — jede Woche ein Stück weiter, nicht nur Meetings.`,
    ],
  },
};

export const SPIEGEL_META = {
  eule: { name: "Eule", glyph: "🦉", tagline: "Big Picture, Zahlen, Daten, Fakten" },
  hund: { name: "Hund", glyph: "🐕", tagline: "Freundlich und fürsorglich" },
  katze: { name: "Katze", glyph: "🐈", tagline: "Emotion, Optionen, Freiheit" },
  biene: { name: "Biene", glyph: "🐝", tagline: "Klare Schritte, direkte Umsetzung" },
};
